/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../../layouts/MainLayout";
import "../../../assets/styles/custom-styles.css";
import useNotification from "../../../hooks/useNotification";
import useLoader from "../../../hooks/useLoader";
import DetailsSectionRenderer from "../../../components/DetailsSectionRenderer";
import { contractDetailsFields } from "../../../constants/forms/contractFields";
import useForm from "../../../hooks/useForm";
import { setDefaultFieldValues } from "../../../utils/objectUtils";
import { entities } from "../../../constants/entities";
import useBaseService from "../../../hooks/services/useBaseService";
import PageHeader from "../../../components/PageHeader";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ListHeader from "../../../components/ListHeader";
import { usePermissions } from "../../../hooks/usePermissions";
import { faEdit, faTrashAlt, faUndo } from "@fortawesome/free-solid-svg-icons";
import useAction from "../../../hooks/useAction";
import ConfirmationModal from "../../../components/modals/ConfirmationModal";
import TableWithInactiveFilter from "../../../components/table/TableWithInactiveFilter";
import useUrlFilters from "../../../hooks/useUrlFilters";
import useGenericFilter from "../../../hooks/filters/useGenericFilter";
import useFilterHandlers from "../../../hooks/useFilterHandlers";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const DetailsEventContractPage = () => {
  const navigate = useNavigate();
  const { canAccess } = usePermissions();
  const { contractId, eventId } = useParams();
  const { showNotification } = useNotification();
  const { showLoader, hideLoader } = useLoader();
  const queryClient = useQueryClient();

  const {
    getByColumn: baseServiceGetByColumn,
    get: baseServiceGet,
    del: baseServiceDel,
  } = useBaseService(navigate);

  const { formData, setFormData } = useForm(
    setDefaultFieldValues(contractDetailsFields),
  );

  const itemsColumns = [
    { key: "alias", label: "Nome do item" },
    { key: "description", label: "Descrição" },
    { key: "quantity", label: "Quantidade" },
    { key: "price", label: "Preço", isMoney: true },
  ];

  const jobsColumns = [
    { key: "alias", label: "Nome do serviço" },
    { key: "description", label: "Descrição" },
    { key: "quantity", label: "Quantidade" },
    { key: "price", label: "Preço", isMoney: true },
  ];

  const {
    setFilters: applyUrlFiltersItems,
    setPagination: setPaginationItems,
    changePage: changePageItems,
    pagination: paginationItems,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    "event_items",
  );

  const {
    setFilters: applyUrlFiltersJobs,
    setPagination: setPaginationJobs,
    changePage: changePageJobs,
    pagination: paginationJobs,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    "event_jobs",
  );

  const {
    handleFilterApply: handleFilterApplyItems,
    handleFilterClear: handleFilterClearItems,
  } = useFilterHandlers(applyUrlFiltersItems);
  const {
    handleFilterApply: handleFilterApplyJobs,
    handleFilterClear: handleFilterClearJobs,
  } = useFilterHandlers(applyUrlFiltersJobs);

  const { localFilterValues: localFilterValuesItems } = useGenericFilter(
    {
      deleted_at: false,
    },
    handleFilterApplyItems,
    handleFilterClearItems,
  );

  const { localFilterValues: localFilterValuesJobs } = useGenericFilter(
    {
      deleted_at: false,
    },
    handleFilterApplyJobs,
    handleFilterClearJobs,
  );

  // --- Query para buscar os detalhes do contrato e informações do evento ---
  const {
    data: contractAndEventDetails,
    isLoading: isLoadingDetails,
    isError: isErrorDetails,
    error: errorDetails,
  } = useQueryWithLoader({
    queryKey: ["contractAndEventDetails", contractId, eventId],
    queryFn: async () => {
      showLoader();
      const contractResponse = await baseServiceGetByColumn(
        entities.contracts.getById(contractId),
      );
      const eventInfoResponse = await baseServiceGet(
        entities.contracts.eventInfosPrevious(contractId, eventId),
      );

      return {
        contract: contractResponse.result,
        info: eventInfoResponse.result,
      };
    },
    enabled: !!contractId && !!eventId,
    staleTime: 5 * 60 * 1000,
  });

  // --- useEffect para preencher o formulário com os detalhes carregados ---
  useEffect(() => {
    if (contractAndEventDetails) {
      setFormData({
        contract: contractAndEventDetails.contract || {},
        info: contractAndEventDetails.info || {},
      });
      hideLoader();
    }
    if (isErrorDetails && errorDetails) {
      showNotification(
        "error",
        `Erro ao carregar detalhes do evento do contrato: ${
          errorDetails.message || errorDetails.response?.data?.message
        }`,
      );
      navigate(`/aluguel/contratos/${contractId}/eventos/`);
    }
  }, [
    contractAndEventDetails,
    setFormData,
    isErrorDetails,
    errorDetails,
    navigate,
    contractId,
    hideLoader,
    showNotification,
  ]);

  // --- Query para buscar os aditivos do evento ---
  const {
    data: eventItems,
    isLoading: isLoadingEventItems,
    isFetching: isFetchingEventItems,
    isError: isErrorEventItems,
    error: eventItemsError,
  } = useQueryWithLoader({
    queryKey: ["contractEventItems", contractId, eventId],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.contracts.eventItemsPrevious(contractId, eventId),
      );
      return response.result;
    },
    enabled: !!contractId && !!eventId,
    staleTime: 5 * 60 * 1000,
    select: (data) => ({
      items: data.data.map((eventItem) => ({
        id: eventItem.id,
        alias: eventItem.alias,
        description: eventItem.description,
        quantity: eventItem.quantity,
        price: eventItem.price,
        isDeleted: !!eventItem.deleted_at,
      })),
      currentPage: data.current_page,
      totalPages: data.last_page,
    }),
  });

  // --- useEffect para preencher o formulário com os detalhes carregados ---
  useEffect(() => {
    if (isErrorEventItems && eventItemsError) {
      showNotification(
        "error",
        `Erro ao carregar aditivos do evento: ${
          eventItemsError.message || eventItemsError.response?.data?.message
        }`,
      );
    }
  }, [
    isErrorEventItems,
    eventItemsError,
    navigate,
    contractId,
    showNotification,
  ]);

  const { data: contractEventsData } = useQueryWithLoader({
    queryKey: ["contractEvents", contractId],
    queryFn: async () => {
      const filters = transformFiltersForUrl({ deleted_at: false });
      const response = await baseServiceGet(
        entities.contracts.events(contractId),
        { filters },
      );
      return response.result.data;
    },
  });

  const {
    data: eventAdditives,
    isError: isErrorEventAdditives,
    error: eventAdditivesError,
  } = useQueryWithLoader({
    queryKey: ["contractEventAdditives", contractId, eventId],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.contracts.eventAdditives(contractId, eventId),
      );
      return response.result;
    },
    enabled: !!contractId && !!eventId,
    staleTime: 5 * 60 * 1000,
    select: (data) => {
      return Array.isArray(data) ? data : [data];
    },
  });

  // --- Função para verificar se é permitido editar o item ---
  const canEdit = useCallback(() => {
    const eventIds = contractEventsData?.map((event) => event.id);
    const isCadastroEvent = eventAdditives?.some(
      (additive) => additive.contract_event_type_id === 1,
    );

    // Contar todos os aditivos do contrato (não só os desse evento)
    const totalAdditives = eventIds?.length;

    // Verifica se o evento atual é o evento de cadastro
    const isSelfEvent = eventIds?.includes(eventId);

    // Se é evento de CADASTRO e houver mais de 1 aditivo no contrato, bloqueia edição
    if (isCadastroEvent && totalAdditives > 1 && !isSelfEvent) {
      return false;
    }

    return true;
  }, [contractEventsData, eventAdditives]);

  // --- useMemo para verificar se é permitido adicionar item ou job ---
  const permitAddItemOrJob = useMemo(() => {
    return eventAdditives?.some(
      (additive) => additive.event_type?.name === "ADITIVO DE QUANTIDADE",
    );
  }, [eventAdditives]);

  // --- useMemo para retornar o id do aditivo de quantidade se permitido ---
  const quantityAdditiveId = useMemo(() => {
    const found = eventAdditives?.find(
      (additive) => additive.event_type?.name === "ADITIVO DE QUANTIDADE",
    );
    return found?.id ?? null;
  }, [eventAdditives]);

  // --- useEffect para preencher o formulário com os detalhes carregados ---
  useEffect(() => {
    if (isErrorEventAdditives && eventAdditivesError) {
      showNotification(
        "error",
        `Erro ao carregar aditivos do evento: ${
          eventAdditivesError.message ||
          eventAdditivesError.response?.data?.message
        }`,
      );
    }
  }, [
    isErrorEventAdditives,
    eventAdditivesError,
    navigate,
    contractId,
    showNotification,
  ]);

  // Seleciona o additiveId mais recente (ou o primeiro se a ordem não importar)
  // Se precisar de outra lógica para selecionar o additiveId, ajuste aqui.
  const latestEventItemAdditiveId = useMemo(() => {
    if (Array.isArray(eventAdditives) && eventAdditives.length > 0) {
      return [...eventAdditives].sort((a, b) => {
        const dateDiff =
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        return dateDiff !== 0 ? dateDiff : b.id - a.id;
      })[0].id;
    }
    return null;
  }, [eventAdditives]);

  const {
    openModalConfirmation: openModalConfirmationItems,
    action: actionItems,
    showConfirmation: showConfirmationItems,
    handleConfirmAction: handleConfirmActionItems,
    handleCancelConfirmation: handleCancelConfirmationItems,
    selectedItem: selectedItemItems,
  } = useAction(navigate);

  const inactiveEventItem = useMutation({
    mutationFn: async ({ contractId, eventId, additiveId, item }) => {
      const response = await baseServiceDel(
        entities.contracts.eventAdditiveItemDelete(
          contractId,
          eventId,
          additiveId,
          item.id,
        ),
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        queryClient.invalidateQueries([
          "contractEventItems",
          contractId,
          eventId,
          latestEventItemAdditiveId,
        ]); // Invalida para refetch
      }
    },
    onError: (error) => {
      console.log("onError ", error);
    },
  });

  const activeEventItem = inactiveEventItem;

  const eventItemsActions = useMemo(
    () => [
      {
        id: "edit",
        icon: faEdit,
        title: "Editar",
        buttonClass: "btn-primary",
        permission: "Atualizar itens de eventos",
        onClick: (item) =>
          navigate(
            `/aluguel/contratos/${contractId}/eventos/${eventId}/aditivos/${latestEventItemAdditiveId}/itens/${item.id}/editar`,
          ),
        show: (item) =>
          !item.isDeleted &&
          canEdit() &&
          canAccess("Atualizar itens de eventos"),
      },
      {
        id: "delete",
        icon: faTrashAlt,
        title: "Excluir",
        buttonClass: "btn-danger",
        permission: "Excluir itens de eventos",
        onClick: (item) =>
          showConfirmationItems(
            item,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await inactiveEventItem.mutateAsync({
                  contractId: contractId,
                  eventId: eventId,
                  additiveId: latestEventItemAdditiveId,
                  item: item,
                });
              } catch (error) {
                console.error("Erro ao excluir item:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (item) =>
          !item.isDeleted && canAccess("Excluir itens de eventos"),
      },
      {
        id: "active",
        icon: faUndo,
        title: "Restaurar",
        buttonClass: "btn-primary",
        permission: "Excluir itens de eventos",
        onClick: (item) =>
          showConfirmationItems(
            item,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await activeEventItem.mutateAsync({
                  contractId: contractId,
                  eventId: eventId,
                  additiveId: latestEventItemAdditiveId,
                  item: item,
                });
              } catch (error) {
                console.error("Erro ao restaurar item:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (item) => item.isDeleted && canAccess("Excluir itens de eventos"),
      },
    ],
    [
      navigate,
      contractId,
      eventId,
      latestEventItemAdditiveId,
      canEdit,
      canAccess,
      showConfirmationItems,
      inactiveEventItem,
      activeEventItem,
    ],
  ); // Adicionado latestAdditiveId às dependências

  // --- Query para buscar Serviços (Jobs) do Evento (agora depende do latestAdditiveId) ---
  const {
    data: eventJobs,
    isLoading: isLoadingEventJobs,
    isFetching: isFetchingEventJobs,
  } = useQueryWithLoader({
    queryKey: ["contractEventJobs", contractId, eventId],
    queryFn: async () => {
      const eventJobs = await baseServiceGet(
        entities.contracts.eventJobs(contractId, eventId),
      );
      return eventJobs.result;
    },
    enabled: !!contractId && !!eventId,
    placeholderData: (previousData) => previousData,
    select: (data) => ({
      jobs: data.data.map((job) => ({
        id: job.id,
        alias: job.alias,
        event_additive_id: job.event_additive_id,
        description: job.description,
        quantity: job.quantity,
        price: job.price,
        isDeleted: !!job.deleted_at,
      })),
      currentPage: data.current_page,
      totalPages: data.last_page,
    }),
  });

  const latestEventJobAdditiveId = useCallback((jobId) => {
    const job = eventJobs?.jobs?.find((job) => job.id === jobId);
    return job?.event_additive_id ?? null;
  }, []);

  const inactiveEventJob = useMutation({
    mutationFn: async ({ contractId, eventId, job }) => {
      const additiveId = latestEventJobAdditiveId(job.id);
      const response = await baseServiceDel(
        entities.contracts.eventAdditiveJobDelete(
          contractId,
          eventId,
          additiveId,
          job.id,
        ),
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        queryClient.invalidateQueries([
          "contractEventJobs",
          contractId,
          eventId,
        ]);
      }
    },
    onError: (error) => {
      console.error("onError ", error);
    },
  });

  const activeEventJob = inactiveEventJob;

  const {
    openModalConfirmation: openModalConfirmationJobs,
    action: actionJobs,
    showConfirmation: showConfirmationJobs,
    handleConfirmAction: handleConfirmActionJobs,
    handleCancelConfirmation: handleCancelConfirmationJobs,
    selectedItem: selectedItemJobs,
  } = useAction(navigate);

  const eventJobsActions = useMemo(
    () => [
      {
        id: "edit",
        icon: faEdit,
        title: "Editar",
        buttonClass: "btn-primary",
        permission: "Atualizar serviços de eventos",
        onClick: (job) =>
          navigate(
            `/aluguel/contratos/${contractId}/eventos/${eventId}/aditivos/${latestEventJobAdditiveId(
              job.id,
            )}/servicos/${job.id}/editar`,
          ),
        show: (job) =>
          !job.isDeleted &&
          canEdit() &&
          canAccess("Atualizar serviços de eventos"),
      },
      {
        id: "delete",
        icon: faTrashAlt,
        title: "Excluir",
        buttonClass: "btn-danger",
        permission: "Excluir serviços de eventos",
        onClick: (job) =>
          showConfirmationJobs(
            job,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await inactiveEventJob.mutateAsync({
                  contractId: contractId,
                  eventId: eventId,
                  job: job,
                });
              } catch (error) {
                console.error("Erro ao excluir envento de servico:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (job) =>
          !job.isDeleted && canAccess("Excluir serviços de eventos"),
      },
      {
        id: "active",
        icon: faUndo,
        title: "Restaurar",
        buttonClass: "btn-primary",
        permission: "Excluir serviços de eventos",
        onClick: (job) =>
          showConfirmationJobs(
            job,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await activeEventJob.mutateAsync({
                  contractId: contractId,
                  eventId: eventId,
                  job: job,
                });
              } catch (error) {
                console.error("Erro ao restaurar envento de servico:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (job) =>
          job.isDeleted && canAccess("Excluir serviços de eventos"),
      },
    ],
    [
      navigate,
      contractId,
      eventId,
      latestEventJobAdditiveId,
      canEdit,
      canAccess,
      showConfirmationJobs,
      inactiveEventJob,
      activeEventJob,
    ],
  );

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Detalhes do Evento do Contrato"
        showBackButton={true}
        backUrl={`/aluguel/contratos/${contractId}/eventos`}
      />
      <button
        className="btn btn-blue-light fw-semibold my-2"
        onClick={() => navigate(`/aluguel/contratos/${contractId}/detalhes`)}>
        Ir para contrato Atualizado
      </button>
      <div className="container-fluid p-1">
        {/* Condição de renderização para DetailsSectionRenderer */}
        {isLoadingDetails ? (
          <div className="text-center py-5">
            Carregando detalhes do contrato e evento...
          </div>
        ) : (
          <DetailsSectionRenderer
            sections={contractDetailsFields}
            formData={formData}
          />
        )}

        {permitAddItemOrJob ? (
          <ListHeader
            title="Itens do Evento"
            buttonText={"Adicionar item"}
            buttonLink={`/aluguel/contratos/${contractId}/eventos/${eventId}/aditivos/${quantityAdditiveId}/itens/adicionar`}
            canAccess={canAccess}
            permission={"Criar itens de eventos"}
            showBackButton={true}
            backUrl={`/aluguel/contratos/${contractId}/eventos/${eventId}/detalhes`}
          />
        ) : (
          <ListHeader
            title="Itens do Evento"
            canAccess={canAccess}
            showBackButton={true}
            buttonIsNotVisible={true}
          />
        )}

        <TableWithInactiveFilter
          data={eventItems?.items || []}
          columns={itemsColumns}
          actions={eventItemsActions}
          totalPages={eventItems?.totalPages}
          currentPage={paginationItems.page - 1}
          onPageChange={changePageItems}
          rowsPerPage={paginationItems.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value;
            setPaginationItems({ page: 1, perPage: newPerPage });
          }}
          filterValues={localFilterValuesItems}
          localFilterValues={localFilterValuesItems}
          applyUrlFilters={applyUrlFiltersItems}
          type="default"
          showFilterInactive={false}
          isLoading={isLoadingEventItems}
          isFetching={isFetchingEventItems}
        />

        <ConfirmationModal
          open={openModalConfirmationItems}
          onClose={handleCancelConfirmationItems}
          onConfirm={handleConfirmActionItems}
          itemName={selectedItemItems ? selectedItemItems.description : ""}
          text={actionItems.text}
        />

        {/* Serviços do Evento */}
        {permitAddItemOrJob ? (
          <ListHeader
            title="Serviços do Evento"
            buttonText={"Adicionar serviço"}
            buttonLink={`/aluguel/contratos/${contractId}/eventos/${eventId}/aditivos/${quantityAdditiveId}/servicos/adicionar`}
            canAccess={canAccess}
            permission={"Criar serviços de eventos"}
            showBackButton={true}
            backUrl={`/aluguel/contratos/${contractId}/eventos/${eventId}/detalhes`}
          />
        ) : (
          <ListHeader
            title="Serviços do Evento"
            canAccess={canAccess}
            showBackButton={true}
            buttonIsNotVisible={true}
          />
        )}

        <TableWithInactiveFilter
          data={eventJobs?.jobs || []}
          columns={jobsColumns}
          actions={eventJobsActions}
          totalPages={eventJobs?.totalPages}
          currentPage={paginationJobs.page - 1}
          onPageChange={changePageJobs}
          rowsPerPage={paginationJobs.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value;
            setPaginationJobs({ page: 1, perPage: newPerPage });
          }}
          filterValues={localFilterValuesJobs}
          localFilterValues={localFilterValuesJobs}
          applyUrlFilters={applyUrlFiltersJobs}
          type="default"
          showFilterInactive={false}
          isLoading={isLoadingEventJobs}
          isFetching={isFetchingEventJobs}
        />

        <ConfirmationModal
          open={openModalConfirmationJobs}
          onClose={handleCancelConfirmationJobs}
          onConfirm={handleConfirmActionJobs}
          itemName={selectedItemJobs ? selectedItemJobs.description : ""}
          text={actionJobs.text}
        />
      </div>
    </MainLayout>
  );
};

export default DetailsEventContractPage;
